import {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import "./CameraDemo.css";
import { useCameraStream } from "./useCameraStream";
import { useBattleColorDetect } from "./useBattleColorDetect";
import {
  FRAME_H,
  FRAME_W,
  FULL_FRAME_CROP_RECT,
  MIN_CROP_W,
  PREVIEW_H,
  PREVIEW_W,
  clampCropRect,
  createInsetCropRect,
  drawVideoFrame,
  frameRectToPreviewRect,
  isFullFrameCropRect,
  type Rect,
} from "./videoCrop";
import {
  type CropHandle,
  type CropInteraction,
  type Gender,
  type SearchStatus,
  type Stats,
  type TeamMember,
  type TeamSnapshot,
} from "../models/camera";
import {
  MATCH_RESULT_GROUPS,
  type PokemonMatch,
  type TeamSearchPokemon,
  type TeamSearchResponse,
} from "../models/teamSearch";
import {
  callTeamSearchApi,
  getDetectedPokemonMatches,
  getMatchGroupEntries,
} from "../services/teamSearchService";
import { Pokedex } from "../vendors/smogon/pokemon-showdown/data/pokedex";
import { Natures } from "../vendors/smogon/pokemon-showdown/data/natures";
import zhPokemonMap from "../assets/locales/zh/pokemon/pokemon.zh.json";
import zhItemMap from "../assets/locales/zh/pokemon/item.zh.json";
import zhMoveMap from "../assets/locales/zh/pokemon/move.zh.json";
import zhNatureMap from "../assets/locales/zh/pokemon/nature.zh.json";
import zhTypeMap from "../assets/locales/zh/pokemon/type.zh.json";
import zhAbilityMap from "../assets/locales/zh/pokemon/ability.zh.json";

const SPRITE_SIZE = 108;
const REFRESH_LOCK_MS = 15_000;
const ALLY_X = 497;
// 这里按 284 处理第 2 个己方槽位，原始输入中的 184 与 108x108 裁切范围会重叠。
const ALLY_YS = [158, 284, 411, 537, 664, 790];
const OPPONENT_OVERLAY_X = 1557;
const OPPONENT_OVERLAY_Y = 154;
const OPPONENT_SLOT_RECTS = [
  { x: 0, y: 0, w: 297, h: 116 },
  { x: 0, y: 125, w: 297, h: 116 },
  { x: 0, y: 251, w: 297, h: 116 },
  { x: 0, y: 378, w: 297, h: 116 },
  { x: 0, y: 503, w: 297, h: 116 },
  { x: 0, y: 629, w: 297, h: 116 },
] as const;
const OPPONENT_SPRITE_OFFSET_X = 66;
const OPPONENT_SPRITE_OFFSET_Y = 4;
// 性别图标采样区域：使用相对单个对手槽位的坐标
const GENDER_OFFSET_X = 205;
const GENDER_OFFSET_Y = 73;
const GENDER_W = 31; // 采样宽度
const GENDER_H = 13; // 采样高度
const STAT_KEYS: Array<keyof Stats> = ["hp", "atk", "def", "spa", "spd", "spe"];
const STAT_LABELS: Record<keyof Stats, string> = {
  hp: "HP",
  atk: "Atk",
  def: "Def",
  spa: "SpA",
  spd: "SpD",
  spe: "Spe",
};
const PREVIEW_TO_FRAME_SCALE_X = FRAME_W / PREVIEW_W;
const PREVIEW_TO_FRAME_SCALE_Y = FRAME_H / PREVIEW_H;
const CROP_HANDLE_SIZE = 16;
const CROP_EDGE_HANDLE_THICKNESS = 20;
const POKEMON_IMAGE_CONFIDENCE_THRESHOLD = 75;
const MS2109_DEVICE_PATTERN = /2109/i;
const TEAM_DIFF_HIGHLIGHT_GROUP_KEYS = new Set<string>([
  "open_teams_pokemon_teamlist_2",
  "championship_pokemon_teamlist",
  "limitless_pokemon_teamlist",
]);

type ResolvedOpponentMember = {
  id: string;
  croppedImageUrl: string;
  displayImageUrl: string;
  gender: Gender;
  pokemonName: string | null;
  lookupPokemonName: string | null;
  translatedPokemonName: string | null;
  displayPokemonName: string | null;
  isUnknownForm: boolean;
  confidencePercent: number | null;
  types: string[];
  abilities: string[];
  baseStats: Stats | null;
  megaForms: MegaFormInfo[];
};

type MatchedPokemonNameIndex = {
  exactNames: Set<string>;
  wildcardBaseNames: Set<string>;
};

type MegaFormInfo = {
  id: string;
  displayName: string;
  formLabel: string;
  colorVariant: "mega" | "mega-x" | "mega-y" | "mega-z";
  types: string[];
  abilities: string[];
  baseStats: Stats | null;
};

function cx(...classNames: Array<string | false | null | undefined>): string {
  return classNames.filter(Boolean).join(" ");
}

function genderBadgeClassName(gender: Gender): string {
  return cx(
    "camera-demo__gender-badge",
    gender === "雄性" && "camera-demo__gender-badge--male",
    gender === "雌性" && "camera-demo__gender-badge--female",
    gender !== "雄性" &&
      gender !== "雌性" &&
      "camera-demo__gender-badge--neutral",
  );
}

function liveBadgeClassName(on: boolean): string {
  return cx(
    "camera-demo__live-badge",
    on ? "camera-demo__live-badge--on" : "camera-demo__live-badge--off",
  );
}

function detectBadgeClassName(color: "blue" | "red"): string {
  return cx(
    "camera-demo__detect-badge",
    color === "blue"
      ? "camera-demo__detect-badge--blue"
      : "camera-demo__detect-badge--red",
  );
}

function CalculatorLaunchIcon(): JSX.Element {
  return (
    <svg
      className="camera-demo__calculator-btn-icon"
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="3.5" y="2.5" width="13" height="15" rx="3" />
      <path d="M6.5 6.5h7" />
      <path d="M7 10.25h1.5" />
      <path d="M11.25 10.25h1.5" />
      <path d="M7 13.25h1.5" />
      <path d="M11.25 13.25h1.5" />
      <path d="M12 5l3-3" />
      <path d="M12.75 2h2.25v2.25" />
    </svg>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function resizeCropRect(
  startRect: Rect,
  handle: Exclude<CropHandle, "move">,
  deltaX: number,
  deltaY: number,
): Rect {
  const aspect = FRAME_W / FRAME_H;
  const hasWest = handle.includes("w");
  const hasEast = handle.includes("e");
  const hasNorth = handle.includes("n");
  const hasSouth = handle.includes("s");

  if ((hasWest || hasEast) && (hasNorth || hasSouth)) {
    const deltaFromX = hasWest ? -deltaX : deltaX;
    const deltaFromY = hasNorth ? -deltaY * aspect : deltaY * aspect;
    const widthDelta =
      Math.abs(deltaFromX) >= Math.abs(deltaFromY) ? deltaFromX : deltaFromY;

    let nextWidth = startRect.w + widthDelta;
    if (hasWest) {
      nextWidth = clamp(nextWidth, MIN_CROP_W, startRect.x + startRect.w);
    } else {
      nextWidth = clamp(nextWidth, MIN_CROP_W, FRAME_W - startRect.x);
    }

    let nextHeight = Math.round(nextWidth / aspect);
    if (hasNorth) {
      nextHeight = Math.min(nextHeight, startRect.y + startRect.h);
    } else {
      nextHeight = Math.min(nextHeight, FRAME_H - startRect.y);
    }
    nextWidth = Math.round(nextHeight * aspect);

    const nextX = hasWest ? startRect.x + startRect.w - nextWidth : startRect.x;
    const nextY = hasNorth
      ? startRect.y + startRect.h - nextHeight
      : startRect.y;

    return clampCropRect({
      x: nextX,
      y: nextY,
      w: nextWidth,
      h: nextHeight,
    });
  }

  if (hasWest || hasEast) {
    const deltaWidth = hasWest ? -deltaX : deltaX;
    let nextWidth = startRect.w + deltaWidth;
    const maxWidth = hasWest
      ? startRect.x + startRect.w
      : FRAME_W - startRect.x;
    nextWidth = clamp(nextWidth, MIN_CROP_W, maxWidth);
    const nextHeight = Math.round(nextWidth / aspect);
    const centerY = startRect.y + startRect.h / 2;
    const nextX = hasWest ? startRect.x + startRect.w - nextWidth : startRect.x;
    const nextY = centerY - nextHeight / 2;

    return clampCropRect({
      x: nextX,
      y: nextY,
      w: nextWidth,
      h: nextHeight,
    });
  }

  const deltaHeight = hasNorth ? -deltaY : deltaY;
  let nextHeight = startRect.h + deltaHeight;
  const minHeight = Math.round(MIN_CROP_W / aspect);
  const maxHeight = hasNorth
    ? startRect.y + startRect.h
    : FRAME_H - startRect.y;
  nextHeight = clamp(nextHeight, minHeight, maxHeight);
  const nextWidth = Math.round(nextHeight * aspect);
  const centerX = startRect.x + startRect.w / 2;
  const nextX = centerX - nextWidth / 2;
  const nextY = hasNorth ? startRect.y + startRect.h - nextHeight : startRect.y;

  return clampCropRect({
    x: nextX,
    y: nextY,
    w: nextWidth,
    h: nextHeight,
  });
}

function updateCropRectByInteraction(
  interaction: CropInteraction,
  clientX: number,
  clientY: number,
): Rect {
  const deltaX = (clientX - interaction.startX) * PREVIEW_TO_FRAME_SCALE_X;
  const deltaY = (clientY - interaction.startY) * PREVIEW_TO_FRAME_SCALE_Y;

  if (interaction.handle === "move") {
    return clampCropRect({
      ...interaction.startRect,
      x: interaction.startRect.x + deltaX,
      y: interaction.startRect.y + deltaY,
    });
  }

  return resizeCropRect(
    interaction.startRect,
    interaction.handle,
    deltaX,
    deltaY,
  );
}

function getCropHandleStyle(
  handle: Exclude<CropHandle, "move">,
): CSSProperties {
  const half = CROP_HANDLE_SIZE / 2;

  switch (handle) {
    case "n":
      return {
        left: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        right: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        top: `${-CROP_EDGE_HANDLE_THICKNESS / 2}px`,
        height: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        cursor: "ns-resize",
      };
    case "e":
      return {
        top: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        bottom: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        right: `${-CROP_EDGE_HANDLE_THICKNESS / 2}px`,
        width: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        cursor: "ew-resize",
      };
    case "s":
      return {
        left: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        right: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        bottom: `${-CROP_EDGE_HANDLE_THICKNESS / 2}px`,
        height: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        cursor: "ns-resize",
      };
    case "w":
      return {
        top: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        bottom: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        left: `${-CROP_EDGE_HANDLE_THICKNESS / 2}px`,
        width: `${CROP_EDGE_HANDLE_THICKNESS}px`,
        cursor: "ew-resize",
      };
    case "nw":
      return {
        left: `${-half}px`,
        top: `${-half}px`,
        cursor: "nwse-resize",
      };
    case "ne":
      return {
        right: `${-half}px`,
        top: `${-half}px`,
        cursor: "nesw-resize",
      };
    case "sw":
      return {
        left: `${-half}px`,
        bottom: `${-half}px`,
        cursor: "nesw-resize",
      };
    case "se":
      return {
        right: `${-half}px`,
        bottom: `${-half}px`,
        cursor: "nwse-resize",
      };
  }
}

function getCropHandleClassName(handle: Exclude<CropHandle, "move">): string {
  return handle.length === 1
    ? "camera-demo__crop-edge-handle"
    : "camera-demo__crop-handle";
}

function formatTime(ts: number | null): string {
  if (!ts) return "未刷新";
  return new Date(ts).toLocaleTimeString("zh-CN", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function detectGenderFromColor(r: number, g: number, b: number): Gender {
  // 雄性：蓝色主导
  if (b >= r + 20 && b >= g + 20 && b >= 100) {
    return "雄性";
  }

  // 雌性：品红/玫红，实测均值约 RGB(160~170, 45~70, 55~80)
  // 亮度下限降到 160，主要依靠 R 高、G 低、B 中等来区分
  const brightness = r + g + b;
  const isFemalePink =
    brightness >= 160 &&
    r >= 120 &&
    g <= 130 &&
    b >= 25 &&
    r >= g + 25 &&
    r >= b - 20; // 排除蓝大于红的菜紫色
  return isFemalePink ? "雌性" : null;
}

function captureSquare(
  sourceCtx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
): { imageUrl: string; jpgBase64: string } {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return { imageUrl: "", jpgBase64: "" };
  }

  ctx.drawImage(sourceCtx.canvas, x, y, size, size, 0, 0, size, size);

  const imageUrl = canvas.toDataURL("image/png");
  const jpgDataUrl = canvas.toDataURL("image/jpeg", 0.92);
  const jpgBase64 = jpgDataUrl.includes(",") ? jpgDataUrl.split(",")[1] : "";

  return { imageUrl, jpgBase64 };
}

function toAssetFileName(value: string, isPokemon: boolean): string {
  if (isPokemon) {
    return value.toLowerCase().replace(/[^a-z0-9-]/g, "");
  }
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function toLocaleKey(value: string, isPokemon: boolean): string {
  if (isPokemon) {
    return value.toLowerCase().replace(/[^a-z0-9-♀♂]/g, "");
  }
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function normalizePokemonTeamCompareName(
  pokemonName: string | null | undefined,
): string | null {
  if (!pokemonName) return null;

  const baseName = pokemonName
    .trim()
    .replace(/-(?:Mega(?:-[XYZ])?|Gmax)$/i, "");
  const localeKey = toLocaleKey(baseName, true);
  return localeKey || null;
}

function getMatchedPokemonNameIndex(
  pokemonMatches: PokemonMatch[],
): MatchedPokemonNameIndex {
  const exactNames = new Set<string>();
  const wildcardBaseNames = new Set<string>();

  for (const match of pokemonMatches) {
    const pokemonName = normalizePokemonTeamCompareName(match.pokemon);
    if (!pokemonName) continue;

    if (pokemonName.endsWith("-")) {
      const baseName = pokemonName.slice(0, -1);
      if (baseName) {
        wildcardBaseNames.add(baseName);
      }
      continue;
    }

    exactNames.add(pokemonName);
  }

  return { exactNames, wildcardBaseNames };
}

function isPokemonNameMatchedByIndex(
  pokemonName: string,
  matchedPokemonNameIndex: MatchedPokemonNameIndex,
): boolean {
  if (matchedPokemonNameIndex.exactNames.has(pokemonName)) return true;

  for (const wildcardBaseName of matchedPokemonNameIndex.wildcardBaseNames) {
    if (
      pokemonName === wildcardBaseName ||
      pokemonName.startsWith(`${wildcardBaseName}-`)
    ) {
      return true;
    }
  }

  return false;
}

function shouldHighlightTeamPokemonDifference(
  groupKey: string | null,
  pokemonName: string | null,
  matchedPokemonNameIndex: MatchedPokemonNameIndex,
): boolean {
  if (
    !groupKey ||
    !TEAM_DIFF_HIGHLIGHT_GROUP_KEYS.has(groupKey) ||
    (matchedPokemonNameIndex.exactNames.size === 0 &&
      matchedPokemonNameIndex.wildcardBaseNames.size === 0)
  ) {
    return false;
  }

  const comparablePokemonName = normalizePokemonTeamCompareName(pokemonName);
  return (
    comparablePokemonName !== null &&
    !isPokemonNameMatchedByIndex(comparablePokemonName, matchedPokemonNameIndex)
  );
}

function translateWithLocaleMap(
  value: string | null,
  isPokemon: boolean,
  localeMap: Record<string, string>,
): string | null {
  if (!value) return null;
  const key = toLocaleKey(value, isPokemon);
  return localeMap[key] ?? value;
}

function translatePokemonName(value: string | null): string | null {
  return translateWithLocaleMap(
    value,
    true,
    zhPokemonMap as Record<string, string>,
  );
}

function translateItemName(value: string | null): string | null {
  return translateWithLocaleMap(
    value,
    false,
    zhItemMap as Record<string, string>,
  );
}

function translateMoveName(value: string | null): string | null {
  return translateWithLocaleMap(
    value,
    false,
    zhMoveMap as Record<string, string>,
  );
}

function translateNatureName(value: string | null): string | null {
  const translated = translateWithLocaleMap(
    value,
    false,
    zhNatureMap as Record<string, string>,
  );
  if (!translated) return null;
  return translated.replace(/\s+[A-Za-z]{3}↑\s+[A-Za-z]{3}↓$/, "");
}

function translateTypeName(value: string | null): string | null {
  return translateWithLocaleMap(
    value,
    false,
    zhTypeMap as Record<string, string>,
  );
}

function translateAbilityName(value: string | null): string | null {
  return translateWithLocaleMap(
    value,
    false,
    zhAbilityMap as Record<string, string>,
  );
}

function getPokemonThumbUrl(pokemon: string | null): string | null {
  if (!pokemon) return null;
  return `./images/pokemon/thumbs/${toAssetFileName(pokemon, true)}.png`;
}

function getPokemonImageUrl(pokemon: string | null): string | null {
  if (!pokemon) return null;
  return `./images/pokemon/imgs/${toAssetFileName(pokemon, true)}.png`;
}

function getItemImageUrl(item: string | null): string | null {
  if (!item) return null;
  return `./images/item/${toAssetFileName(item, false)}.png`;
}

function getTeraImageUrl(teraType: string | null): string | null {
  if (!teraType) return null;
  return `./images/type/terastal/${toAssetFileName(teraType, false)}.png`;
}

function getConfidencePercent(confidence: number | null | undefined): number | null {
  if (typeof confidence !== "number" || !Number.isFinite(confidence)) return null;

  const rawPercent = confidence <= 1 ? confidence * 100 : confidence;
  return Math.max(0, Math.min(100, rawPercent));
}

function formatConfidencePercent(
  confidencePercent: number | null | undefined,
): string | null {
  if (typeof confidencePercent !== "number" || !Number.isFinite(confidencePercent)) {
    return null;
  }

  return confidencePercent.toFixed(4);
}

function createStatsFromArray(values?: number[] | null, fallback = 0): Stats {
  return {
    hp: values?.[0] ?? fallback,
    atk: values?.[1] ?? fallback,
    def: values?.[2] ?? fallback,
    spa: values?.[3] ?? fallback,
    spd: values?.[4] ?? fallback,
    spe: values?.[5] ?? fallback,
  };
}

function convertModernEvToLegacy(value: number): number {
  if (value <= 0) return 0;
  if (value === 1) return 4;
  return 4 + (value - 1) * 8;
}

function getLegacyEvs(pokemon: TeamSearchPokemon): Stats {
  if (pokemon.evs?.length === 6) {
    const modern = createStatsFromArray(pokemon.evs);
    return {
      hp: convertModernEvToLegacy(modern.hp),
      atk: convertModernEvToLegacy(modern.atk),
      def: convertModernEvToLegacy(modern.def),
      spa: convertModernEvToLegacy(modern.spa),
      spd: convertModernEvToLegacy(modern.spd),
      spe: convertModernEvToLegacy(modern.spe),
    };
  }

  if (pokemon.evs2?.length === 6) {
    const modern = createStatsFromArray(pokemon.evs2);
    return {
      hp: convertModernEvToLegacy(modern.hp),
      atk: convertModernEvToLegacy(modern.atk),
      def: convertModernEvToLegacy(modern.def),
      spa: convertModernEvToLegacy(modern.spa),
      spd: convertModernEvToLegacy(modern.spd),
      spe: convertModernEvToLegacy(modern.spe),
    };
  }

  return createStatsFromArray(undefined, 0);
}

function getIvsForStats(pokemon: TeamSearchPokemon): Stats {
  if (pokemon.ivs?.length === 6) {
    return createStatsFromArray(pokemon.ivs, 31);
  }
  return createStatsFromArray(undefined, 31);
}

function getNatureModifier(nature: string | null): {
  plus?: keyof Stats;
  minus?: keyof Stats;
} {
  if (!nature) return {};
  const modifier = Natures[nature.toLowerCase() as keyof typeof Natures];
  return modifier ?? {};
}

function getColoredStatClassName(
  statKey: keyof Stats,
  natureModifier: { plus?: keyof Stats; minus?: keyof Stats },
): string {
  if (natureModifier.plus === statKey) {
    return "camera-demo__selected-stat-positive";
  }
  if (natureModifier.minus === statKey) {
    return "camera-demo__selected-stat-negative";
  }
  return "camera-demo__selected-stat-neutral";
}

function calculateLevel50Speed(
  baseSpe: number,
  ev: number,
  nature: number,
): number {
  const iv = 31;
  const raw = Math.floor(((baseSpe * 2 + iv + Math.floor(ev / 4)) * 50) / 100) + 5;
  return Math.floor(raw * nature);
}

function renderBaseStatsWithSpeedRefs(stats: Stats): JSX.Element {
  const statValues = STAT_KEYS.map((key) => stats[key]).join("/");
  const minNegativeSpeed = calculateLevel50Speed(stats.spe, 0, 0.9);
  const minNeutralSpeed = calculateLevel50Speed(stats.spe, 0, 1);
  const neutralSpeed = calculateLevel50Speed(stats.spe, 252, 1);
  const plusNatureSpeed = calculateLevel50Speed(stats.spe, 252, 1.1);

  return (
    <>
      {statValues} (
      <span className="camera-demo__selected-stat-negative">
        {minNegativeSpeed}
      </span>
      ,{minNeutralSpeed},{neutralSpeed},
      <span className="camera-demo__selected-stat-positive">
        {plusNatureSpeed}
      </span>
      )
    </>
  );
}

function getPokedexKey(pokemonName: string): string {
  return toLocaleKey(pokemonName, false);
}

function normalizePokemonLookupName(pokemonName: string | null): {
  lookupPokemonName: string | null;
  isUnknownForm: boolean;
} {
  if (!pokemonName) {
    return { lookupPokemonName: null, isUnknownForm: false };
  }

  if (pokemonName.endsWith("-")) {
    const normalized = pokemonName.slice(0, -1);
    return {
      lookupPokemonName: normalized || null,
      isUnknownForm: true,
    };
  }

  return {
    lookupPokemonName: pokemonName,
    isUnknownForm: false,
  };
}

function getPokedexEntry(pokemonName: string | null): {
  name?: string;
  otherFormes?: string[];
  forme?: string;
  types?: string[];
  abilities?: Record<string, string>;
  baseStats?: Stats;
} | null {
  if (!pokemonName) return null;

  return (Pokedex[
    getPokedexKey(pokemonName) as keyof typeof Pokedex
  ] as {
    name?: string;
    otherFormes?: string[];
    forme?: string;
    types?: string[];
    abilities?: Record<string, string>;
    baseStats?: Stats;
  } | undefined) ?? null;
}

function getMegaColorVariant(formLabel: string): MegaFormInfo["colorVariant"] {
  const normalizedLabel = formLabel.toLowerCase();

  if (normalizedLabel.includes("mega-x")) return "mega-x";
  if (normalizedLabel.includes("mega-y")) return "mega-y";
  if (normalizedLabel.includes("mega-z")) return "mega-z";
  return "mega";
}

function formatFormDisplayName(
  basePokemonName: string | null,
  fallbackPokemonName: string,
  formLabel: string,
): string {
  const translatedBaseName =
    translatePokemonName(basePokemonName) ??
    translatePokemonName(fallbackPokemonName) ??
    basePokemonName ??
    fallbackPokemonName;

  return `${translatedBaseName} ${formLabel}`;
}

function getMegaSortRank(formLabel: string): number {
  const normalizedLabel = formLabel.toLowerCase();

  if (normalizedLabel === "mega") return 0;
  if (normalizedLabel === "mega-x") return 1;
  if (normalizedLabel === "mega-y") return 2;
  if (normalizedLabel === "mega-z") return 3;
  return 99;
}

function getMegaSourcePokemonName(pokemonName: string | null): string | null {
  switch (pokemonName) {
    case "Floette-Eternal":
      return "Floette";
    case "Meowstic":
      return "Meowstic-M";
    case "Tatsugiri":
      return "Tatsugiri-Curly";
    default:
      return pokemonName;
  }

  return pokemonName;
}

function getMegaForms(pokemonName: string | null): MegaFormInfo[] {
  const megaSourcePokemonName = getMegaSourcePokemonName(pokemonName);
  const baseEntry = getPokedexEntry(megaSourcePokemonName);
  if (!baseEntry?.otherFormes?.length) return [];

  return baseEntry.otherFormes
    .map((formeName) => {
      const formeEntry = getPokedexEntry(formeName);
      const formLabel =
        formeEntry?.forme ??
        formeName.slice((baseEntry.name ?? pokemonName ?? "").length + 1);

      if (!formLabel || !formLabel.toLowerCase().startsWith("mega")) {
        return null;
      }

      return {
        id: formeName,
        displayName: formatFormDisplayName(
          pokemonName,
          formeName,
          formLabel,
        ),
        formLabel,
        colorVariant: getMegaColorVariant(formLabel),
        types: (formeEntry?.types ?? [])
          .map((typeName) => translateTypeName(typeName))
          .filter((typeName): typeName is string => !!typeName),
        abilities: Object.values(formeEntry?.abilities ?? {})
          .map((ability) => translateAbilityName(ability))
          .filter((ability): ability is string => !!ability),
        baseStats: formeEntry?.baseStats ?? null,
      } satisfies MegaFormInfo;
    })
    .filter((forme): forme is MegaFormInfo => forme !== null)
    .sort((left, right) => {
      const rankDiff =
        getMegaSortRank(left.formLabel) - getMegaSortRank(right.formLabel);
      return rankDiff !== 0 ? rankDiff : left.formLabel.localeCompare(right.formLabel);
    });
}

function resolveOpponentMember(
  member: TeamMember,
  match: PokemonMatch | undefined,
): ResolvedOpponentMember {
  const confidencePercent = getConfidencePercent(match?.confidence);
  const pokemonName = match?.pokemon ?? null;
  const { lookupPokemonName, isUnknownForm } =
    normalizePokemonLookupName(pokemonName);
  const translatedPokemonName = translatePokemonName(lookupPokemonName);
  const displayPokemonName =
    translatedPokemonName ?? lookupPokemonName ?? pokemonName;
  const pokedexEntry = getPokedexEntry(lookupPokemonName);
  const displayImageUrl =
    lookupPokemonName &&
    confidencePercent !== null &&
    confidencePercent > POKEMON_IMAGE_CONFIDENCE_THRESHOLD
      ? (getPokemonImageUrl(lookupPokemonName) ?? member.imageUrl)
      : member.imageUrl;

  return {
    id: member.id,
    croppedImageUrl: member.imageUrl,
    displayImageUrl,
    gender: member.gender,
    pokemonName,
    lookupPokemonName,
    translatedPokemonName,
    displayPokemonName,
    isUnknownForm,
    confidencePercent,
    types: (pokedexEntry?.types ?? [])
      .map((typeName) => translateTypeName(typeName))
      .filter((typeName): typeName is string => !!typeName),
    abilities: Object.values(pokedexEntry?.abilities ?? {})
      .map((ability) => translateAbilityName(ability))
      .filter((ability): ability is string => !!ability),
    baseStats: pokedexEntry?.baseStats ?? null,
    megaForms: getMegaForms(lookupPokemonName),
  };
}

function calculatePokemonStats(pokemon: TeamSearchPokemon): Stats | null {
  if (!pokemon.pokemon) return null;
  const pokedexEntry = Pokedex[
    getPokedexKey(pokemon.pokemon) as keyof typeof Pokedex
  ] as { baseStats?: Stats } | undefined;
  const baseStats = pokedexEntry?.baseStats;
  if (!baseStats) return null;

  const evs = getLegacyEvs(pokemon);
  const ivs = getIvsForStats(pokemon);
  const natureModifier = getNatureModifier(pokemon.nature ?? null);
  const level = 50;

  const hp =
    Math.floor(
      ((baseStats.hp * 2 + ivs.hp + Math.floor(evs.hp / 4)) * level) / 100,
    ) +
    level +
    10;
  const calculateOtherStat = (statKey: keyof Stats): number => {
    const baseValue = baseStats[statKey];
    const evValue = evs[statKey];
    const ivValue = ivs[statKey];
    const base =
      Math.floor(
        ((baseValue * 2 + ivValue + Math.floor(evValue / 4)) * level) / 100,
      ) + 5;

    if (natureModifier.plus === statKey) {
      return Math.floor(base * 1.1);
    }
    if (natureModifier.minus === statKey) {
      return Math.floor(base * 0.9);
    }
    return base;
  };

  return {
    hp,
    atk: calculateOtherStat("atk"),
    def: calculateOtherStat("def"),
    spa: calculateOtherStat("spa"),
    spd: calculateOtherStat("spd"),
    spe: calculateOtherStat("spe"),
  };
}

function formatIvsLine(ivs?: number[] | null): string | null {
  if (!ivs || ivs.length !== 6) return null;
  const stats = createStatsFromArray(ivs, 31);
  const entries = STAT_KEYS.filter((key) => stats[key] < 31).map(
    (key) => `${stats[key]} ${STAT_LABELS[key]}`,
  );
  return entries.length > 0 ? `个体 ${entries.join(" / ")}` : null;
}

function getExportEvs(pokemon: TeamSearchPokemon): number[] | null {
  if (pokemon.evs?.length === 6) return pokemon.evs;
  if (pokemon.evs2?.length === 6) return pokemon.evs2;
  return null;
}

function buildPokemonPasteText(pokemon: TeamSearchPokemon): string {
  const lines: string[] = [];
  const pokemonName = pokemon.pokemon ?? "Unknown";

  if (pokemon.item) {
    lines.push(`${pokemonName} @ ${pokemon.item}`);
  } else {
    lines.push(pokemonName);
  }

  lines.push("Level: 50");

  if (pokemon.ability) {
    lines.push(`Ability: ${pokemon.ability}`);
  }

  if (pokemon.tera_type) {
    lines.push(`Tera Type: ${pokemon.tera_type}`);
  }

  const exportEvs = getExportEvs(pokemon);
  if (exportEvs) {
    const stats = createStatsFromArray(exportEvs);
    const entries = STAT_KEYS.filter((key) => stats[key] > 0).map(
      (key) => `${stats[key]} ${STAT_LABELS[key]}`,
    );
    if (entries.length > 0) {
      lines.push(`EVs: ${entries.join(" / ")}`);
    }
  }

  if (pokemon.nature) {
    lines.push(`${pokemon.nature} Nature`);
  }

  if (pokemon.ivs?.length === 6) {
    const ivStats = createStatsFromArray(pokemon.ivs, 31);
    const ivEntries = STAT_KEYS.filter((key) => ivStats[key] < 31).map(
      (key) => `${ivStats[key]} ${STAT_LABELS[key]}`,
    );
    if (ivEntries.length > 0) {
      lines.push(`IVs: ${ivEntries.join(" / ")}`);
    }
  }

  for (const move of pokemon.moves ?? []) {
    if (move) {
      lines.push(`- ${move}`);
    }
  }

  return lines.join("\n");
}

function buildTeamPasteText(pokemons: TeamSearchPokemon[]): string {
  return pokemons
    .filter((pokemon) => !!pokemon.pokemon)
    .map((pokemon) => buildPokemonPasteText(pokemon))
    .join("\n\n");
}

function buildPokemonPackedFormat(pokemon: TeamSearchPokemon): string {
  const name = pokemon.pokemon ?? "";
  const species = pokemon.pokemon ?? "";
  const item = pokemon.item ?? "";
  const ability = pokemon.ability ?? "";
  const moves = (pokemon.moves ?? []).filter((m) => m).join(",");
  const nature = pokemon.nature ?? "";
  const evs = pokemon.evs
    ? pokemon.evs.join(",")
    : pokemon.evs2
      ? pokemon.evs2.join(",")
      : "";
  const gender = "";
  const ivs = pokemon.ivs ? pokemon.ivs.join(",") : "";
  const shiny = "";
  const level = "50";
  const teraType = pokemon.tera_type ?? "";
  const misc = teraType ? `,,,,,${teraType}` : "";

  return `${name}|${species}|${item}|${ability}|${moves}|${nature}|${evs}|${gender}|${ivs}|${shiny}|${level}|${misc}`;
}

function buildTeamPackedFormat(pokemons: TeamSearchPokemon[]): string {
  return pokemons
    .filter((pokemon) => !!pokemon.pokemon)
    .map((pokemon) => buildPokemonPackedFormat(pokemon))
    .join("]");
}

function openCalculatorWithTeam(pokemons: TeamSearchPokemon[]): void {
  const packed = buildTeamPackedFormat(pokemons);
  if (!packed) return;

  const url =
    "https://pokestats.top/calc/#" +
    encodeURIComponent(`2${packed}`) +
    `?t=${Date.now()}`;

  window.open(url, "vgc-damage-calculator");
}

function renderColoredStatsLine(
  label: string,
  stats: Stats | null,
  natureModifier: { plus?: keyof Stats; minus?: keyof Stats },
  options?: {
    hideZero?: boolean;
    showLabel?: boolean;
    showStatLabels?: boolean;
  },
) {
  if (!stats) return null;

  const entries = STAT_KEYS.filter(
    (key) => !options?.hideZero || stats[key] > 0,
  ).map((key) => ({
    key,
    value: stats[key],
  }));

  if (entries.length === 0) return null;

  return (
    <div className="camera-demo__selected-stats-line">
      {options?.showLabel === false ? null : <span>{label} </span>}
      {entries.map((entry, index) => (
        <span key={`${label}-${entry.key}`}>
          {index > 0 ? (
            <span className="camera-demo__selected-stat-separator"> / </span>
          ) : null}
          <span className={getColoredStatClassName(entry.key, natureModifier)}>
            {entry.value}
            {options?.showStatLabels === false
              ? null
              : ` ${STAT_LABELS[entry.key]}`}
          </span>
        </span>
      ))}
    </div>
  );
}

function renderNatureLine(
  translatedNature: string | null,
  natureModifier: { plus?: keyof Stats; minus?: keyof Stats },
) {
  if (!translatedNature) return null;

  return (
    <div className="camera-demo__selected-pokemon-line">
      <span>{translatedNature}</span>
      {natureModifier.plus ? (
        <>
          <span className="camera-demo__selected-stat-separator"> </span>
          <span className="camera-demo__selected-stat-positive">
            {STAT_LABELS[natureModifier.plus]}↑
          </span>
        </>
      ) : null}
      {natureModifier.minus ? (
        <>
          <span className="camera-demo__selected-stat-separator"> </span>
          <span className="camera-demo__selected-stat-negative">
            {STAT_LABELS[natureModifier.minus]}↓
          </span>
        </>
      ) : null}
    </div>
  );
}

function readAverageColor(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
): { r: number; g: number; b: number } {
  const { data } = ctx.getImageData(x, y, width, height);
  let sumR = 0;
  let sumG = 0;
  let sumB = 0;
  let total = 0;

  for (let i = 0; i < data.length; i += 4) {
    sumR += data[i];
    sumG += data[i + 1];
    sumB += data[i + 2];
    total += 1;
  }

  if (total === 0) {
    return { r: 0, g: 0, b: 0 };
  }

  return {
    r: Math.round(sumR / total),
    g: Math.round(sumG / total),
    b: Math.round(sumB / total),
  };
}

function captureTeamSnapshot(
  video: HTMLVideoElement,
  cropRect: Rect,
): TeamSnapshot | null {
  if (
    video.readyState < 2 ||
    video.videoWidth === 0 ||
    video.videoHeight === 0
  ) {
    return null;
  }

  const frameCanvas = document.createElement("canvas");
  frameCanvas.width = FRAME_W;
  frameCanvas.height = FRAME_H;
  const frameCtx = frameCanvas.getContext("2d", { willReadFrequently: true });

  if (!frameCtx) return null;

  drawVideoFrame(frameCtx, video, FRAME_W, FRAME_H, cropRect);

  const left = ALLY_YS.map((y, index) => {
    const capture = captureSquare(frameCtx, ALLY_X, y, SPRITE_SIZE);
    return {
      id: `ally-${index + 1}`,
      imageUrl: capture.imageUrl,
      jpgBase64: capture.jpgBase64,
      gender: null,
    };
  });

  const right = OPPONENT_SLOT_RECTS.map((slot, index) => {
    const spriteX = OPPONENT_OVERLAY_X + slot.x + OPPONENT_SPRITE_OFFSET_X;
    const spriteY = OPPONENT_OVERLAY_Y + slot.y + OPPONENT_SPRITE_OFFSET_Y;
    const color = readAverageColor(
      frameCtx,
      OPPONENT_OVERLAY_X + slot.x + GENDER_OFFSET_X,
      OPPONENT_OVERLAY_Y + slot.y + GENDER_OFFSET_Y,
      GENDER_W,
      GENDER_H,
    );
    const capture = captureSquare(frameCtx, spriteX, spriteY, SPRITE_SIZE);
    return {
      id: `opponent-${index + 1}`,
      imageUrl: capture.imageUrl,
      jpgBase64: capture.jpgBase64,
      gender: detectGenderFromColor(color.r, color.g, color.b),
    };
  });

  return { left, right };
}

function TeamPanel({
  title,
  members,
  showGender,
  showIndex = true,
  compact = false,
  reverseLayout = false,
  narrowCompact = false,
  statusImageUrl = null,
}: {
  title: string;
  members: TeamMember[];
  showGender: boolean;
  showIndex?: boolean;
  compact?: boolean;
  reverseLayout?: boolean;
  narrowCompact?: boolean;
  statusImageUrl?: string | null;
}) {
  const showMeta = showIndex || showGender;

  return (
    <div
      className={cx(
        "camera-demo__team-panel",
        compact && "camera-demo__team-panel--compact",
      )}
    >
      <div className="camera-demo__team-title">{title}</div>
      {statusImageUrl ? (
        <div className="camera-demo__status-preview-wrap">
          <img
            src={statusImageUrl}
            alt={`${title}状态截图`}
            className="camera-demo__status-preview"
          />
        </div>
      ) : null}
      <div
        className={cx(
          "camera-demo__team-list",
          compact && "camera-demo__team-list--compact",
          narrowCompact && "camera-demo__team-list--compact-ally",
        )}
      >
        {members.length === 0 ? (
          <div className="camera-demo__member-placeholder-card">
            <div className="camera-demo__member-placeholder">
              等待左右框体同时命中后刷新
            </div>
          </div>
        ) : (
          members.map((member, index) => (
            <div
              key={member.id}
              className={cx(
                "camera-demo__member-card",
                compact && "camera-demo__member-card--compact",
                reverseLayout && "camera-demo__member-card--reverse",
                index > 0 && "camera-demo__member-card--divider",
              )}
            >
              <div className="camera-demo__member-thumb-wrap">
                <img
                  src={member.imageUrl}
                  alt={`${title}${index + 1}`}
                  className="camera-demo__member-thumb"
                />
              </div>
              {showMeta ? (
                <div
                  className={cx(
                    "camera-demo__member-meta",
                    reverseLayout && "camera-demo__member-meta--reverse",
                  )}
                >
                  {showIndex ? (
                    <div className="camera-demo__member-index">#{index + 1}</div>
                  ) : null}
                  {showGender ? (
                    <span className={genderBadgeClassName(member.gender)}>
                      {member.gender ?? "无性别"}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function OpponentTeamPanel({
  title,
  members,
  pokemonMatches,
  showDetails,
  onShowDetailsChange,
  statusImageUrl,
}: {
  title: string;
  members: TeamMember[];
  pokemonMatches: PokemonMatch[];
  showDetails: boolean;
  onShowDetailsChange: (checked: boolean) => void;
  statusImageUrl: string | null;
}) {
  const resolvedMembers = useMemo(() => {
    const pokemonMatchMap = new Map(
      pokemonMatches.map((match, index) => [match.num || index + 1, match]),
    );

    return members.map((member, index) =>
      resolveOpponentMember(member, pokemonMatchMap.get(index + 1)),
    );
  }, [members, pokemonMatches]);

  return (
    <div
      className={cx(
        "camera-demo__team-panel",
        "camera-demo__team-panel--opponent",
        !showDetails && "camera-demo__team-panel--compact",
      )}
    >
      <div className="camera-demo__team-title camera-demo__team-title--with-toggle">
        <span>{title}</span>
        <label className="camera-demo__team-title-toggle">
          <input
            type="checkbox"
            checked={showDetails}
            onChange={(event) => onShowDetailsChange(event.target.checked)}
            className="camera-demo__team-title-checkbox"
          />
          详细
        </label>
      </div>
      {statusImageUrl ? (
        <div className="camera-demo__status-preview-wrap camera-demo__status-preview-wrap--opponent">
          <img
            src={statusImageUrl}
            alt={`${title}状态截图`}
            className="camera-demo__status-preview"
          />
        </div>
      ) : null}
      <div
        className={cx(
          "camera-demo__team-list",
          showDetails
            ? "camera-demo__team-list--opponent"
            : "camera-demo__team-list--compact",
        )}
      >
        {resolvedMembers.length === 0 ? (
          <div className="camera-demo__member-placeholder-card">
            <div className="camera-demo__member-placeholder">
              等待左右框体同时命中后刷新
            </div>
          </div>
        ) : (
          resolvedMembers.map((member, index) =>
            showDetails ? (
              <div
                key={member.id}
                className={cx(
                  "camera-demo__opponent-card",
                  index > 0 && "camera-demo__member-card--divider",
                )}
              >
                <div className="camera-demo__opponent-thumb-wrap">
                  <img
                    src={member.displayImageUrl}
                    alt={
                      member.translatedPokemonName ??
                      member.pokemonName ??
                      `${title}${index + 1}`
                    }
                    className="camera-demo__opponent-thumb"
                  />
                  {member.confidencePercent !== null &&
                  member.confidencePercent < 90 ? (
                    <span className="camera-demo__opponent-confidence-badge">
                      {formatConfidencePercent(member.confidencePercent)}%
                    </span>
                  ) : null}
                </div>
                <div className="camera-demo__opponent-content">
                  {member.pokemonName ? (
                    <>
                      <div className="camera-demo__opponent-name-row">
                        <div className="camera-demo__opponent-name">
                          {member.displayPokemonName ?? member.pokemonName}
                          {member.isUnknownForm ? " 形态未知" : ""}
                          {" "}
                          <span className="camera-demo__opponent-raw-name">
                            (
                            {member.pokemonName.endsWith("-")
                              ? `${member.pokemonName}?`
                              : member.pokemonName}
                            )
                          </span>
                        </div>
                        <span className={genderBadgeClassName(member.gender)}>
                          {member.gender ?? "无性别"}
                        </span>
                      </div>
                      {member.types.length > 0 ? (
                        <div className="camera-demo__opponent-meta-line">
                          属性：{member.types.join(" / ")}
                          {member.megaForms
                            .filter((megaForm) => megaForm.types.length > 0)
                            .map((megaForm) => (
                              <span
                                key={`${megaForm.id}-types`}
                                className={cx(
                                  "camera-demo__mega-inline",
                                  `camera-demo__mega-inline--${megaForm.colorVariant}`,
                                )}
                              >
                                {megaForm.types.join(" / ")}
                              </span>
                            ))}
                        </div>
                      ) : null}
                      {member.abilities.length > 0 ? (
                        <div className="camera-demo__opponent-meta-line">
                          特性：{member.abilities.join(" / ")}
                          {member.megaForms
                            .filter((megaForm) => megaForm.abilities.length > 0)
                            .map((megaForm) => (
                              <span
                                key={`${megaForm.id}-abilities`}
                                className={cx(
                                  "camera-demo__mega-inline",
                                  `camera-demo__mega-inline--${megaForm.colorVariant}`,
                                )}
                              >
                                {megaForm.abilities.join(" / ")}
                              </span>
                            ))}
                        </div>
                      ) : null}
                      {member.baseStats || member.megaForms.length > 0 ? (
                        <div className="camera-demo__stats-block">
                          <div className="camera-demo__meta-label">种族值：</div>
                          <div className="camera-demo__stats-stack">
                            {member.baseStats ? (
                              <div className="camera-demo__stats-values">
                                {renderBaseStatsWithSpeedRefs(member.baseStats)}
                              </div>
                            ) : null}
                            {member.megaForms.map((megaForm) =>
                              megaForm.baseStats ? (
                                <div
                                  key={megaForm.id}
                                  className={cx(
                                    "camera-demo__mega-stats-line",
                                    `camera-demo__mega-stats-line--${megaForm.colorVariant}`,
                                  )}
                                >
                                  <div className="camera-demo__mega-stats-values">
                                    {renderBaseStatsWithSpeedRefs(megaForm.baseStats)}
                                  </div>
                                </div>
                              ) : null,
                            )}
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <div className="camera-demo__opponent-name-row">
                        <div className="camera-demo__opponent-name camera-demo__opponent-name--muted">
                          未识别到宝可梦名称
                        </div>
                        <span className={genderBadgeClassName(member.gender)}>
                          {member.gender ?? "无性别"}
                        </span>
                      </div>
                      <div className="camera-demo__opponent-meta-line camera-demo__opponent-meta-line--muted">
                        当前展示为截取画面，等待更高识别度结果
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div
                key={member.id}
                className={cx(
                  "camera-demo__member-card",
                  "camera-demo__member-card--compact",
                  index > 0 && "camera-demo__member-card--divider",
                )}
              >
                <div className="camera-demo__member-thumb-wrap">
                  <img
                    src={member.croppedImageUrl}
                    alt={
                      member.translatedPokemonName ??
                      member.pokemonName ??
                      `${title}${index + 1}`
                    }
                    className="camera-demo__member-thumb"
                  />
                </div>
              </div>
            ),
          )
        )}
      </div>
    </div>
  );
}

function OpponentMatchResults({
  result,
  status,
  resetToken,
  pokemonMatches,
}: {
  result: TeamSearchResponse | null;
  status: SearchStatus;
  resetToken: number;
  pokemonMatches: PokemonMatch[];
}) {
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [hoveredMatchId, setHoveredMatchId] = useState<string | null>(null);
  const [hoveredSelectedPokemonId, setHoveredSelectedPokemonId] = useState<
    string | null
  >(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastPosition, setToastPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const teamLongPressTimerRef = useRef<number | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const visibleGroups = MATCH_RESULT_GROUPS.map((group) => ({
    ...group,
    matches: getMatchGroupEntries(result, group.key),
  })).filter((group) => group.matches.length > 0);
  const allMatches = visibleGroups.flatMap((group) => group.matches);
  const matchedPokemonNameIndex = useMemo(
    () => getMatchedPokemonNameIndex(pokemonMatches),
    [pokemonMatches],
  );
  const selectedMatchIdForRender =
    status === "searching" || !selectedMatchId
      ? null
      : allMatches.some((match) => match.id === selectedMatchId)
        ? selectedMatchId
        : null;
  const selectedMatch =
    allMatches.find((match) => match.id === selectedMatchIdForRender) ?? null;
  const selectedMatchGroupKey =
    selectedMatch && selectedMatchIdForRender
      ? (visibleGroups.find((group) =>
          group.matches.some((match) => match.id === selectedMatchIdForRender),
        )?.key ?? null)
      : null;

  useEffect(() => {
    setSelectedMatchId(null);
    setHoveredMatchId(null);
    setHoveredSelectedPokemonId(null);
  }, [resetToken]);

  const clearTeamLongPressTimer = () => {
    if (teamLongPressTimerRef.current !== null) {
      window.clearTimeout(teamLongPressTimerRef.current);
      teamLongPressTimerRef.current = null;
    }
  };

  const showToast = (message: string, position?: { x: number; y: number }) => {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToastMessage(message);
    if (position) {
      setToastPosition({
        x: Math.min(position.x + 16, window.innerWidth - 260),
        y: Math.min(position.y + 16, window.innerHeight - 80),
      });
    }
    toastTimerRef.current = window.setTimeout(() => {
      setToastMessage(null);
      setToastPosition(null);
      toastTimerRef.current = null;
    }, 1800);
  };

  const copySelectedTeamPasteText = async (position?: {
    x: number;
    y: number;
  }): Promise<void> => {
    if (!selectedMatch) return;
    const pasteText = buildTeamPasteText(selectedMatch.pokemons);
    if (!pasteText) return;

    try {
      await navigator.clipboard.writeText(pasteText);
      showToast("队伍信息复制成功", position);
    } catch {
      showToast("队伍信息复制失败", position);
    }
  };

  const handleTeamPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!selectedMatch || event.button !== 0) return;
    clearTeamLongPressTimer();
    const pointerPosition = { x: event.clientX, y: event.clientY };
    teamLongPressTimerRef.current = window.setTimeout(() => {
      void copySelectedTeamPasteText(pointerPosition);
    }, 500);
  };

  const handleTeamPointerUp = () => {
    clearTeamLongPressTimer();
  };

  const handleSelectMatch = (matchId: string) => {
    setSelectedMatchId(matchId);
  };

  return (
    <div className="camera-demo__match-results-wrap">
      {toastMessage && toastPosition ? (
        <div
          className="camera-demo__toast-success"
          style={{
            left: `${toastPosition.x}px`,
            top: `${toastPosition.y}px`,
          }}
        >
          {toastMessage}
        </div>
      ) : null}
      <div className="camera-demo__match-results-header-row">
        <div className="camera-demo__match-results-header">
          对手队伍匹配结果
        </div>
        <a
          href="https://pokestats.top/calc/"
          target="_blank"
          rel="noreferrer"
          className="camera-demo__match-results-link"
        >
          VGC伤害计算器
        </a>
      </div>
      {status === "searching" ? (
        <div className="camera-demo__match-status-card">正在搜索队伍...</div>
      ) : status === "done" && visibleGroups.length === 0 ? (
        <div className="camera-demo__match-status-card">
          没有检索到类似的队伍
        </div>
      ) : status === "idle" ? (
        <div className="camera-demo__match-status-card">
          等待检测到敌我双方队伍后开始搜索
        </div>
      ) : (
        <>
          {selectedMatch ? (
            <div
              className="camera-demo__selected-team-panel"
              onPointerDown={handleTeamPointerDown}
              onPointerUp={handleTeamPointerUp}
              onPointerLeave={handleTeamPointerUp}
              onPointerCancel={handleTeamPointerUp}
            >
              <div className="camera-demo__selected-team-header">
                <div className="camera-demo__selected-team-title-row">
                  <div className="camera-demo__selected-team-title">
                    选中队伍详细信息
                  </div>
                  <button
                    className="camera-demo__calculator-btn"
                    onClick={() => openCalculatorWithTeam(selectedMatch.pokemons)}
                    title="在伤害计算器中打开此队伍作为敌方队伍"
                  >
                    <CalculatorLaunchIcon />
                    <span>计算器中导入</span>
                  </button>
                </div>
                <div className="camera-demo__selected-team-meta">
                  {selectedMatch.author}
                  {selectedMatch.xAccount ? ` (${selectedMatch.xAccount})` : ""}
                  {selectedMatch.sharedDate
                    ? ` · ${selectedMatch.sharedDate}`
                    : ""}
                  {selectedMatch.eventName
                    ? ` · ${selectedMatch.eventName}`
                    : ""}
                  {selectedMatch.record ? ` · ${selectedMatch.record}` : ""}
                  {selectedMatch.ranking ? ` · ${selectedMatch.ranking}` : ""}
                </div>
              </div>
              <div className="camera-demo__selected-team-grid">
                {Array.from({ length: 6 }, (_, index) => {
                  const pokemon = selectedMatch.pokemons[index] ?? null;
                  const selectedPokemonCardId = `${selectedMatch.id}-detail-${index + 1}`;
                  const isHoveredSelectedPokemon =
                    hoveredSelectedPokemonId === selectedPokemonCardId;
                  const pokemonName = pokemon?.pokemon ?? null;
                  const translatedPokemonName =
                    translatePokemonName(pokemonName);
                  const imageUrl = getPokemonImageUrl(pokemonName);
                  const shouldHighlightDifference =
                    shouldHighlightTeamPokemonDifference(
                      selectedMatchGroupKey,
                      pokemonName,
                      matchedPokemonNameIndex,
                    );
                  const teraUrl = getTeraImageUrl(pokemon?.tera_type ?? null);
                  const itemUrl = getItemImageUrl(pokemon?.item ?? null);
                  const translatedAbility = translateAbilityName(
                    pokemon?.ability ?? null,
                  );
                  const translatedItem = translateItemName(
                    pokemon?.item ?? null,
                  );
                  const translatedTeraType = translateTypeName(
                    pokemon?.tera_type ?? null,
                  );
                  const translatedNature = translateNatureName(
                    pokemon?.nature ?? null,
                  );
                  const natureModifier = getNatureModifier(
                    pokemon?.nature ?? null,
                  );
                  const translatedMoves = (pokemon?.moves ?? [])
                    .map((move) => translateMoveName(move))
                    .filter((move): move is string => !!move);
                  const calculatedStats = pokemon
                    ? calculatePokemonStats(pokemon)
                    : null;
                  const effortStats =
                    pokemon?.evs?.length === 6
                      ? createStatsFromArray(pokemon.evs)
                      : pokemon?.evs2?.length === 6
                        ? createStatsFromArray(pokemon.evs2)
                        : null;
                  const statsLine = renderColoredStatsLine(
                    "",
                    calculatedStats,
                    natureModifier,
                    { showLabel: false, showStatLabels: false },
                  );
                  const evsLine = renderColoredStatsLine(
                    "努力",
                    effortStats,
                    natureModifier,
                    { hideZero: true },
                  );
                  const ivsLine = formatIvsLine(pokemon?.ivs ?? null);
                  const natureLine = renderNatureLine(
                    translatedNature,
                    natureModifier,
                  );

                  return (
                    <div
                      key={selectedPokemonCardId}
                      className={cx(
                        "camera-demo__selected-pokemon-card",
                        isHoveredSelectedPokemon &&
                          "camera-demo__selected-pokemon-card--hovered",
                      )}
                      onMouseEnter={() =>
                        setHoveredSelectedPokemonId(selectedPokemonCardId)
                      }
                      onMouseLeave={() =>
                        setHoveredSelectedPokemonId((current) =>
                          current === selectedPokemonCardId ? null : current,
                        )
                      }
                    >
                      <div
                        className={cx(
                          "camera-demo__selected-pokemon-thumb-wrap",
                          shouldHighlightDifference &&
                            "camera-demo__selected-pokemon-thumb-wrap--unmatched",
                        )}
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={
                              translatedPokemonName ??
                              pokemonName ??
                              `宝可梦 ${index + 1}`
                            }
                            className="camera-demo__selected-pokemon-thumb"
                          />
                        ) : null}
                        {teraUrl ? (
                          <img
                            src={teraUrl}
                            alt={
                              translatedTeraType ??
                              pokemon?.tera_type ??
                              "太晶属性"
                            }
                            className="camera-demo__match-tera-icon"
                          />
                        ) : null}
                        {itemUrl ? (
                          <img
                            src={itemUrl}
                            alt={translatedItem ?? pokemon?.item ?? "道具"}
                            className="camera-demo__match-item-icon"
                          />
                        ) : null}
                      </div>
                      <div className="camera-demo__selected-pokemon-name">
                        {translatedPokemonName ??
                          pokemonName ??
                          `队伍成员 ${index + 1}`}
                      </div>
                      {translatedAbility ? (
                        <div className="camera-demo__selected-pokemon-line">
                          特性：{translatedAbility}
                        </div>
                      ) : null}
                      {translatedItem ? (
                        <div className="camera-demo__selected-pokemon-line">
                          道具：{translatedItem}
                        </div>
                      ) : null}
                      {statsLine}
                      {natureLine}
                      {translatedTeraType ? (
                        <div className="camera-demo__selected-pokemon-tag-row">
                          <span className="camera-demo__selected-pokemon-tag">
                            太晶 {translatedTeraType}
                          </span>
                        </div>
                      ) : null}
                      <div className="camera-demo__selected-stats-block">
                        {evsLine}
                        {ivsLine ? (
                          <div className="camera-demo__selected-stats-line">
                            {ivsLine}
                          </div>
                        ) : null}
                      </div>
                      {translatedMoves.length > 0 ? (
                        <div className="camera-demo__selected-moves">
                          {translatedMoves.slice(0, 4).map((move) => (
                            <span
                              key={`${selectedMatch.id}-${index}-${move}`}
                              className="camera-demo__selected-move"
                            >
                              {move}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          {visibleGroups.map((group) => (
            <div key={group.key} className="camera-demo__match-group">
              <div className="camera-demo__match-group-header">
                <div className="camera-demo__match-group-title">
                  {group.title}
                </div>
                <div className="camera-demo__match-group-count">
                  {group.matches.length} 个结果
                </div>
              </div>
              <div className="camera-demo__match-grid">
                {group.matches.map((match) => {
                  const isSelected = selectedMatchIdForRender === match.id;
                  const isHovered = hoveredMatchId === match.id;

                  return (
                    <div
                      key={match.id}
                      className={cx(
                        "camera-demo__match-card",
                        isSelected && "camera-demo__match-card--active",
                        !isSelected &&
                          isHovered &&
                          "camera-demo__match-card--hovered",
                      )}
                      onClick={() => handleSelectMatch(match.id)}
                      onMouseEnter={() => setHoveredMatchId(match.id)}
                      onMouseLeave={() =>
                        setHoveredMatchId((current) =>
                          current === match.id ? null : current,
                        )
                      }
                    >
                      <div className="camera-demo__match-row-top">
                        <div className="camera-demo__match-author">
                          {match.author}
                          {match.xAccount ? ` (${match.xAccount})` : ""}
                        </div>
                        <div className="camera-demo__match-date">
                          {match.sharedDate ?? "-"}
                        </div>
                      </div>
                      <div className="camera-demo__match-row-meta">
                        <div
                          className="camera-demo__match-meta-value"
                          title={match.eventName ?? "-"}
                        >
                          {match.eventName ?? "-"}
                        </div>
                        <div
                          className="camera-demo__match-meta-value"
                          title={match.record ?? "-"}
                        >
                          {match.record ?? "-"}
                        </div>
                        <div
                          className="camera-demo__match-meta-value"
                          title={match.ranking ?? "-"}
                        >
                          {match.ranking ?? "-"}
                        </div>
                      </div>
                      <div className="camera-demo__match-pokemon-row">
                        {Array.from({ length: 6 }, (_, index) => {
                          const pokemon = match.pokemons[index] ?? null;
                          const pokemonName = pokemon?.pokemon ?? null;
                          const thumbUrl = getPokemonThumbUrl(pokemonName);
                          const shouldHighlightDifference =
                            shouldHighlightTeamPokemonDifference(
                              group.key,
                              pokemonName,
                              matchedPokemonNameIndex,
                            );
                          const teraUrl = getTeraImageUrl(
                            pokemon?.tera_type ?? null,
                          );
                          const itemUrl = getItemImageUrl(
                            pokemon?.item ?? null,
                          );

                          return (
                            <div
                              key={`${match.id}-pokemon-${index + 1}`}
                              className={cx(
                                "camera-demo__match-pokemon-slot",
                                shouldHighlightDifference &&
                                  "camera-demo__match-pokemon-slot--unmatched",
                              )}
                              title={pokemonName ?? `队伍成员 ${index + 1}`}
                            >
                              {thumbUrl ? (
                                <div className="camera-demo__match-pokemon-anchor">
                                  <img
                                    src={thumbUrl}
                                    alt={pokemonName ?? `队伍成员 ${index + 1}`}
                                    className="camera-demo__match-pokemon-img"
                                  />
                                </div>
                              ) : null}
                              {teraUrl ? (
                                <img
                                  src={teraUrl}
                                  alt={pokemon?.tera_type ?? "太晶属性"}
                                  className="camera-demo__match-tera-thumb-icon"
                                />
                              ) : null}
                              {itemUrl ? (
                                <img
                                  src={itemUrl}
                                  alt={pokemon?.item ?? "道具"}
                                  className="camera-demo__match-item-thumb-icon"
                                />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default function CameraDemo() {
  const cameraStreamOptions = useMemo(
    () => ({
      video: {
        width: { exact: 1920 },
        height: { exact: 1080 },
        aspectRatio: { exact: 16 / 9 },
        frameRate: { exact: 60 },
      },
      audio: true,
    }),
    [],
  );

  const {
    videoRef,
    stream,
    error,
    isLoading,
    devices,
    audioDevices,
    currentDeviceId,
    currentAudioDeviceId,
    switchCamera,
    switchAudio,
    stopCamera,
    startCamera,
  } = useCameraStream(cameraStreamOptions);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const cropBoxRef = useRef<HTMLDivElement | null>(null);
  const lastActiveVolumeRef = useRef(100);
  const previousAudioDeviceIdRef = useRef<string | null>(null);
  const [cropRect, setCropRect] = useState<Rect>({ ...FULL_FRAME_CROP_RECT });
  const [draftCropRect, setDraftCropRect] = useState<Rect>(() =>
    createInsetCropRect(),
  );
  const [isCropEditing, setIsCropEditing] = useState(false);
  const [cropInteraction, setCropInteraction] =
    useState<CropInteraction | null>(null);
  const [isStopped, setIsStopped] = useState(false);
  const [audioMuted, setAudioMuted] = useState(true);
  const [audioVolume, setAudioVolume] = useState(100);
  const [snapshot, setSnapshot] = useState<TeamSnapshot>({
    left: [],
    right: [],
  });
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);
  const [opponentPokemonMatches, setOpponentPokemonMatches] = useState<
    PokemonMatch[]
  >([]);
  const [opponentMatchResult, setOpponentMatchResult] =
    useState<TeamSearchResponse | null>(null);
  const [opponentSearchStatus, setOpponentSearchStatus] =
    useState<SearchStatus>("idle");
  const [showOpponentDetails, setShowOpponentDetails] = useState(true);
  const [autoDetectEnabled, setAutoDetectEnabled] = useState(true);
  const [sourceTemplateActivationToken, setSourceTemplateActivationToken] =
    useState(0);
  const [opponentResultsResetToken, setOpponentResultsResetToken] = useState(0);
  const [lockUntil, setLockUntil] = useState<number | null>(null);
  const [clock, setClock] = useState(() => Date.now());
  const lockUntilRef = useRef<number | null>(null);
  const teamSearchInFlightRef = useRef(false);
  const previousTemplateDetectedRef = useRef(false);
  const hasCustomCrop = !isFullFrameCropRect(cropRect);
  const activeCropRect = isCropEditing ? draftCropRect : cropRect;
  const previewCropRect = useMemo(
    () => frameRectToPreviewRect(activeCropRect, PREVIEW_W, PREVIEW_H),
    [activeCropRect],
  );
  const cropStatusText = hasCustomCrop
    ? `已剪裁 ${cropRect.w} x ${cropRect.h}`
    : "原始画面";

  const battleDetect = useBattleColorDetect(videoRef, stream, {
    fps: 1,
    cropRect,
    sourceTemplateActivationToken,
  });
  const [consecutiveMatches, setConsecutiveMatches] = useState(0);

  const {
    detectionTick,
    templateScorePermille,
    templateScorePercent,
    templateDetected,
    sourceTemplateScorePermille,
    sourceTemplateScorePercent,
    sourceTemplateDetected,
    sourceTemplateConsecutiveMatches,
    allyStatusImageUrl,
    opponentStatusImageUrl,
    debug,
  } = battleDetect;
  const clearOpponentSearchDisplay = useCallback(
    (nextStatus: SearchStatus = "idle") => {
      setOpponentPokemonMatches([]);
      setOpponentMatchResult(null);
      setOpponentSearchStatus(nextStatus);
      setOpponentResultsResetToken((current) => current + 1);
    },
    [],
  );

  const resetRecognitionState = useCallback(() => {
    setSnapshot({ left: [], right: [] });
    setLastRefreshAt(null);
    clearOpponentSearchDisplay("idle");
    setConsecutiveMatches(0);
    lockUntilRef.current = null;
    setLockUntil(null);
  }, [clearOpponentSearchDisplay]);

  const beginCropInteraction = useCallback(
    (handle: CropHandle) => (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isCropEditing) return;
      event.preventDefault();
      event.stopPropagation();
      setCropInteraction({
        pointerId: event.pointerId,
        handle,
        startX: event.clientX,
        startY: event.clientY,
        startRect: draftCropRect,
      });
    },
    [draftCropRect, isCropEditing],
  );

  const handleCropKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isCropEditing) return;

      const step = event.shiftKey ? 20 : 4;
      let deltaX = 0;
      let deltaY = 0;

      switch (event.key) {
        case "ArrowUp":
          deltaY = -step;
          break;
        case "ArrowDown":
          deltaY = step;
          break;
        case "ArrowLeft":
          deltaX = -step;
          break;
        case "ArrowRight":
          deltaX = step;
          break;
        default:
          return;
      }

      event.preventDefault();
      event.stopPropagation();
      setDraftCropRect((currentRect) =>
        clampCropRect({
          ...currentRect,
          x: currentRect.x + deltaX,
          y: currentRect.y + deltaY,
        }),
      );
    },
    [isCropEditing],
  );

  const handleCropAction = useCallback(() => {
    if (isCropEditing) {
      const nextCropRect = clampCropRect(draftCropRect);
      setCropRect(nextCropRect);
      setDraftCropRect(nextCropRect);
      setIsCropEditing(false);
      setCropInteraction(null);
      resetRecognitionState();
      return;
    }

    if (hasCustomCrop) {
      setCropRect({ ...FULL_FRAME_CROP_RECT });
      setDraftCropRect(createInsetCropRect());
      setIsCropEditing(false);
      setCropInteraction(null);
      resetRecognitionState();
      return;
    }

    setDraftCropRect(createInsetCropRect());
    setIsCropEditing(true);
  }, [draftCropRect, hasCustomCrop, isCropEditing, resetRecognitionState]);

  const handleCancelCrop = useCallback(() => {
    setDraftCropRect(cropRect);
    setIsCropEditing(false);
    setCropInteraction(null);
  }, [cropRect]);

  const handleStop = () => {
    stopCamera();
    setIsStopped(true);
    lockUntilRef.current = null;
    setLockUntil(null);
  };
  const handleStart = () => {
    startCamera(currentDeviceId ?? null, currentAudioDeviceId);
    setIsStopped(false);
  };
  const handleSwitch = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextDeviceId = e.target.value;
    const selectedDevice = devices.find((device) => device.deviceId === nextDeviceId);
    const matchesMs2109 = MS2109_DEVICE_PATTERN.test(selectedDevice?.label ?? "");

    await switchCamera(nextDeviceId);
    setIsStopped(false);

    if (matchesMs2109) {
      window.alert(
        "提示：\n\n" +
          "随着后续版本持续更新，识别模型规模会进一步增长。MS2109 采集卡的图像质量较差、稳定性也不足，继续兼容会明显增加模型数据量，并进一步提升服务器的 CPU、内存负载，以及学习数据维护成本。\n\n" +
          "在当前资源条件下，继续维护这部分兼容性，已经难以同时兼顾服务稳定性、识别效率和后续内容更新。\n\n" +
          "基于长期维护和整体使用体验考虑，后续版本将逐步停止对 MS2109 采集图片的支持。建议升级至 MS2130 或 MS2131 采集卡（通常 30 元左右）。购买时请务必确认芯片型号，部分商家会将 MS2109 伪装成 MS2130 出售。"
      );
    }
  };
  const handleAudioSwitch = (e: React.ChangeEvent<HTMLSelectElement>) => {
    void switchAudio(e.target.value || null);
  };
  const handleAutoDetectToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = e.target.checked;
    setAutoDetectEnabled(enabled);

    if (!enabled) {
      lockUntilRef.current = null;
      setLockUntil(null);
    }
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextVolume = Number(e.target.value);
    setAudioVolume(nextVolume);

    if (nextVolume > 0) {
      lastActiveVolumeRef.current = nextVolume;
      setAudioMuted(false);
    } else {
      setAudioMuted(true);
    }
  };
  const toggleMute = () => {
    if (!hasAudioTrack || !currentAudioDeviceId) return;

    if (audioMuted || audioVolume === 0) {
      const restoredVolume =
        lastActiveVolumeRef.current > 0 ? lastActiveVolumeRef.current : 100;
      setAudioVolume(restoredVolume);
      setAudioMuted(false);
      return;
    }

    if (audioVolume > 0) {
      lastActiveVolumeRef.current = audioVolume;
    }
    setAudioMuted(true);
  };

  const trackSettings = stream?.getVideoTracks()[0]?.getSettings() ?? {};
  const audioTrackSettings = stream?.getAudioTracks()[0]?.getSettings() ?? {};
  const hasAudioTrack = (stream?.getAudioTracks().length ?? 0) > 0;
  const isLive = !!stream && !isStopped;
  const canControlAudio = !!currentAudioDeviceId && hasAudioTrack && !isStopped;
  const displayedAudioVolume = canControlAudio
    ? audioMuted
      ? 0
      : audioVolume
    : 0;
  const refreshCountdownMs = lockUntil ? Math.max(0, lockUntil - clock) : 0;
  const refreshCountdownSeconds = Math.ceil(refreshCountdownMs / 1000);
  const isRefreshLocked = refreshCountdownMs > 0;
  const canManualRecognize =
    isLive &&
    !isCropEditing &&
    !isLoading &&
    opponentSearchStatus !== "searching";
  const currentAudioLabel = currentAudioDeviceId
    ? (audioDevices.find((device) => device.deviceId === currentAudioDeviceId)
        ?.label ?? "已选择音频设备")
    : "未选择音频设备";

  const runSnapshotSearch = useCallback(async (options: {
    respectCooldown?: boolean;
  } = {}): Promise<boolean> => {
    const { respectCooldown = false } = options;
    const now = Date.now();
    const isLocked =
      lockUntilRef.current !== null && now < lockUntilRef.current;
    if ((respectCooldown && isLocked) || teamSearchInFlightRef.current) {
      return false;
    }

    const video = videoRef.current;
    if (!video) return false;

    const nextSnapshot = captureTeamSnapshot(video, cropRect);
    if (!nextSnapshot) return false;

    const nextLockUntil = now + REFRESH_LOCK_MS;
    lockUntilRef.current = nextLockUntil;
    setLockUntil(nextLockUntil);
    teamSearchInFlightRef.current = true;

    setSnapshot(nextSnapshot);
    setLastRefreshAt(now);
    clearOpponentSearchDisplay("searching");
    setSourceTemplateActivationToken((current) => current + 1);

    try {
      const opponentResult = await callTeamSearchApi(
        "opponent",
        nextSnapshot.right,
      );

      setOpponentPokemonMatches(getDetectedPokemonMatches(opponentResult));
      setOpponentMatchResult(opponentResult);
      setOpponentSearchStatus("done");
      return true;
    } finally {
      teamSearchInFlightRef.current = false;
    }
  }, [clearOpponentSearchDisplay, cropRect, videoRef]);

  useEffect(() => {
    if (!isLive) {
      previousTemplateDetectedRef.current = false;
      setConsecutiveMatches(0);
      return;
    }
    if (templateDetected) {
      setConsecutiveMatches((prev) => prev + 1);
    } else {
      setConsecutiveMatches(0);
    }
  }, [detectionTick, templateDetected, isLive]);

  useEffect(() => {
    if (!isLive) return;

    const wasTemplateDetected = previousTemplateDetectedRef.current;
    previousTemplateDetectedRef.current = templateDetected;

    if (!wasTemplateDetected && templateDetected) {
      const now = Date.now();
      const isLocked =
        lockUntilRef.current !== null && now < lockUntilRef.current;
      if (isLocked || teamSearchInFlightRef.current) return;

      clearOpponentSearchDisplay("idle");
    }
  }, [clearOpponentSearchDisplay, isLive, templateDetected]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setClock(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = displayedAudioVolume / 100;
    audio.muted = audioMuted || isStopped;

    if (!stream || stream.getAudioTracks().length === 0) {
      audio.pause();
      audio.srcObject = null;
      return;
    }

    if (audio.srcObject !== stream) {
      audio.srcObject = stream;
    }

    audio.play().catch(() => {});
  }, [audioMuted, displayedAudioVolume, isStopped, stream]);

  useEffect(() => {
    const previousAudioDeviceId = previousAudioDeviceIdRef.current;
    previousAudioDeviceIdRef.current = currentAudioDeviceId;

    if (!currentAudioDeviceId || !hasAudioTrack) {
      return;
    }

    if (previousAudioDeviceId === currentAudioDeviceId) {
      return;
    }

    const restoredVolume =
      lastActiveVolumeRef.current > 0 ? lastActiveVolumeRef.current : 100;
    setAudioVolume(restoredVolume);
    setAudioMuted(false);
  }, [currentAudioDeviceId, hasAudioTrack]);

  useEffect(() => {
    if (!cropInteraction) return;

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerId !== cropInteraction.pointerId) return;
      setDraftCropRect(
        updateCropRectByInteraction(
          cropInteraction,
          event.clientX,
          event.clientY,
        ),
      );
    };

    const clearInteraction = (event: PointerEvent) => {
      if (event.pointerId !== cropInteraction.pointerId) return;
      setCropInteraction(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", clearInteraction);
    window.addEventListener("pointercancel", clearInteraction);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", clearInteraction);
      window.removeEventListener("pointercancel", clearInteraction);
    };
  }, [cropInteraction]);

  useEffect(() => {
    if (!isCropEditing) return;
    cropBoxRef.current?.focus();
  }, [isCropEditing]);

  useEffect(() => {
    if (!autoDetectEnabled || !isLive || isCropEditing || !templateDetected) {
      return;
    }

    const now = Date.now();
    const isLocked =
      lockUntilRef.current !== null && now < lockUntilRef.current;

    if (isLocked) {
      const nextLockUntil = now + REFRESH_LOCK_MS;
      lockUntilRef.current = nextLockUntil;
      setLockUntil(nextLockUntil);
      return;
    }

    if (consecutiveMatches < 3) return;

    void runSnapshotSearch({ respectCooldown: true });
  }, [
    autoDetectEnabled,
    consecutiveMatches,
    detectionTick,
    isCropEditing,
    isLive,
    runSnapshotSearch,
    templateDetected,
  ]);

  const refreshStatusText =
    opponentSearchStatus === "searching"
      ? "正在切图并检索队伍"
      : isCropEditing
        ? "正在调整剪裁区域，应用后再进行模板匹配与切图"
        : !autoDetectEnabled
          ? "自动识别已关闭，可手动点击“手动识别队伍”"
          : templateDetected && isRefreshLocked
            ? "模板匹配再次命中，已重置锁定并跳过 API 调用"
            : isRefreshLocked
              ? "锁定中"
              : templateDetected
                ? consecutiveMatches >= 3
                  ? "模板匹配已连续命中 3 秒，触发切图与检索"
                  : sourceTemplateConsecutiveMatches > 0
                    ? `来源模板确认中... (${sourceTemplateConsecutiveMatches}/2)`
                    : `模板匹配已命中，等待确认... (${consecutiveMatches}/3)`
                : "等待模板匹配命中";
  const refreshCooldownText = isRefreshLocked
    ? `（冷却剩余 ${refreshCountdownSeconds} 秒）`
    : "";
  const cropButtonLabel = isCropEditing
    ? "✅ 应用剪裁"
    : hasCustomCrop
      ? "↺ 还原画面"
      : "✂ 剪裁画面";

  const handleManualRecognize = () => {
    if (!canManualRecognize) return;
    void runSnapshotSearch();
  };

  const getErrorMessage = (err: Error): string => {
    switch (err.name) {
      case "NotAllowedError":
        return "用户拒绝摄像头权限，请在浏览器地址栏允许后刷新。";
      case "NotFoundError":
        return "未找到可用摄像头，请检查设备连接。";
      case "NotReadableError":
        return "摄像头被其他应用占用，请关闭后重试。";
      case "OverconstrainedError":
        return "摄像头不支持指定的分辨率或帧率，已自动回退。";
      default:
        return err.message;
    }
  };

  return (
    <div
      className={cx(
        "camera-demo__app",
        !showOpponentDetails && "camera-demo__app--compact-opponent",
      )}
    >
      {/* 标题 */}
      <h1 className="camera-demo__title">
        📷 宝可梦VGC对战助手
        <span className={liveBadgeClassName(isLive)}>
          {isLive ? "LIVE" : "OFF"}
        </span>
      </h1>

      <div
        className={cx(
          "camera-demo__capture-stage",
          !showOpponentDetails && "camera-demo__capture-stage--compact-opponent",
        )}
      >
        <TeamPanel
          title="己方"
          members={snapshot.left}
          showGender={false}
          showIndex={false}
          compact
          reverseLayout
          narrowCompact
          statusImageUrl={allyStatusImageUrl}
        />

        {/* 视频画面 */}
        <div className="camera-demo__video-wrapper">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-demo__video"
          />
          {(isCropEditing || hasCustomCrop) && (
            <div className="camera-demo__crop-layer">
              <div
                ref={cropBoxRef}
                className={cx(
                  "camera-demo__crop-box",
                  !isCropEditing && "camera-demo__crop-box--locked",
                )}
                style={{
                  left: `${previewCropRect.x}px`,
                  top: `${previewCropRect.y}px`,
                  width: `${previewCropRect.w}px`,
                  height: `${previewCropRect.h}px`,
                  cursor: isCropEditing ? "move" : "default",
                  pointerEvents: isCropEditing ? "auto" : "none",
                }}
                tabIndex={isCropEditing ? 0 : -1}
                aria-label="剪裁区域，支持方向键微调位置"
                onPointerDown={beginCropInteraction("move")}
                onKeyDown={handleCropKeyDown}
              >
                {isCropEditing &&
                  (
                    ["n", "e", "s", "w", "nw", "ne", "sw", "se"] as Array<
                      Exclude<CropHandle, "move">
                    >
                  ).map((handle) => (
                    <div
                      key={handle}
                      className={getCropHandleClassName(handle)}
                      style={getCropHandleStyle(handle)}
                      onPointerDown={beginCropInteraction(handle)}
                    />
                  ))}
              </div>
            </div>
          )}

          {/* 加载中 / 已停止 遮罩 */}
          {isLoading && (
            <div className="camera-demo__overlay">⏳ 正在请求摄像头权限...</div>
          )}
          {isStopped && !isLoading && (
            <div className="camera-demo__overlay">
              摄像头已停止，点击“开启”恢复
            </div>
          )}

          {/* 识别命中提示：只在命中时显示，叠加在视频下方 */}
          {isLive && (
            <div className="camera-demo__detect-row">
              <div>
                {templateDetected && (
                  <span className={detectBadgeClassName("blue")}>
                    🧩 模板区域已识别
                  </span>
                )}
              </div>
              <div />
            </div>
          )}
        </div>

        <OpponentTeamPanel
          title="对手"
          members={snapshot.right}
          pokemonMatches={opponentPokemonMatches}
          showDetails={showOpponentDetails}
          onShowDetailsChange={setShowOpponentDetails}
          statusImageUrl={opponentStatusImageUrl}
        />
      </div>
      <audio
        ref={audioRef}
        autoPlay
        playsInline
        className="camera-demo__audio-hidden"
      />

      {/* 错误提示 */}
      {error && (
        <div className="camera-demo__error-box">
          ❌ <strong>错误：</strong>
          {getErrorMessage(error)}
        </div>
      )}

      {/* 控制栏 */}
      <div className="camera-demo__controls-wrap">
        <div className="camera-demo__controls">
          <select
            className="camera-demo__select"
            value={currentDeviceId ?? ""}
            onChange={handleSwitch}
            disabled={isLoading}
          >
            <option value="" disabled>
              {devices.length === 0 ? "无可用摄像头" : "-- 切换摄像头 --"}
            </option>
            {devices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `摄像头 ${i + 1}`}
              </option>
            ))}
          </select>
          {isStopped ? (
            <button className="camera-demo__btn-green" onClick={handleStart}>
              ▶ 启动设备
            </button>
          ) : (
            <button
              className="camera-demo__btn-red"
              onClick={handleStop}
              disabled={!stream || isLoading}
            >
              ■ 停止设备
            </button>
          )}
          <select
            className="camera-demo__select"
            value={currentAudioDeviceId ?? ""}
            onChange={handleAudioSwitch}
            disabled={isLoading || isStopped}
          >
            <option value="">
              {audioDevices.length === 0
                ? "无可用音频设备"
                : "-- 自动匹配/选择音频 --"}
            </option>
            {audioDevices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `音频设备 ${i + 1}`}
              </option>
            ))}
          </select>
          <div className="camera-demo__volume-control">
            <button
              type="button"
              className="camera-demo__btn-mute"
              onClick={toggleMute}
              disabled={!canControlAudio}
              aria-label={audioMuted || !canControlAudio ? "取消静音" : "静音"}
              title={audioMuted || !canControlAudio ? "取消静音" : "静音"}
            >
              {audioMuted || !canControlAudio ? "🔇" : "🔊"}
            </button>
            <span className="camera-demo__volume-label">音量</span>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={displayedAudioVolume}
              onChange={handleVolumeChange}
              disabled={!canControlAudio}
              className="camera-demo__range"
            />
          </div>
          <label className="camera-demo__toggle-label">
            <input
              type="checkbox"
              checked={autoDetectEnabled}
              onChange={handleAutoDetectToggle}
              className="camera-demo__checkbox"
            />
            自动识别
          </label>
          <button
            type="button"
            className="camera-demo__btn-blue"
            onClick={handleManualRecognize}
            disabled={!canManualRecognize}
          >
            🔍 手动识别队伍
          </button>
          <button
            type="button"
            className={
              hasCustomCrop ? "camera-demo__btn-green" : "camera-demo__btn-blue"
            }
            onClick={handleCropAction}
            disabled={isLoading || !stream}
          >
            {cropButtonLabel}
          </button>
          {isCropEditing && (
            <button
              type="button"
              className="camera-demo__btn-slate"
              onClick={handleCancelCrop}
            >
              取消剪裁
            </button>
          )}
        </div>
      </div>

      <OpponentMatchResults
        result={opponentMatchResult}
        status={opponentSearchStatus}
        resetToken={opponentResultsResetToken}
        pokemonMatches={opponentPokemonMatches}
      />

      {/* 设备信息 */}
      <div className="camera-demo__info-box">
        <div>🎥 检测到摄像头：{devices.length} 个</div>
        <div>🎙 检测到音频输入：{audioDevices.length} 个</div>
        <div>🤖 自动识别：{autoDetectEnabled ? "已开启" : "已关闭"}</div>
        <div>✂ 当前画面：{cropStatusText}</div>
        {isCropEditing && <div>🧭 剪裁模式：拖动框体或四角调整，保持 16:9</div>}
        {trackSettings.width && (
          <div>
            📐 分辨率：{trackSettings.width} × {trackSettings.height} px
          </div>
        )}
        {trackSettings.width && trackSettings.height && (
          <div>
            📏 画面比例：
            {(trackSettings.width / trackSettings.height).toFixed(3)}
          </div>
        )}
        {trackSettings.frameRate && (
          <div>🏞 帧率：{Math.round(trackSettings.frameRate)} fps</div>
        )}
        <div>🎛 当前音频设备：{currentAudioLabel}</div>
        <div>
          🔊 音频状态：
          {canControlAudio
            ? audioMuted || isStopped
              ? `静音，音量 ${displayedAudioVolume}%`
              : `播放中，音量 ${displayedAudioVolume}%`
            : "未接入音频"}
        </div>
        {audioTrackSettings.sampleRate && (
          <div>🎚 音频采样率：{audioTrackSettings.sampleRate} Hz</div>
        )}
        <div>🕒 上次刷新时间：{formatTime(lastRefreshAt)}</div>
        <div>
          🔁 刷新状态：{refreshStatusText}
          {refreshCooldownText}
        </div>
        <div className="camera-demo__debug-section">
          <div>
            🧩 模板 1：
            <strong
              className={
                templateDetected
                  ? "camera-demo__debug-mode--active"
                  : "camera-demo__debug-mode--inactive"
              }
            >
              {templateDetected ? "已命中" : "未命中"}
            </strong>
            &nbsp;&nbsp; 相似度：{templateScorePercent}%&nbsp;&nbsp; 阈值：
            {debug.templateThresholdPercent}%
          </div>
          <div>
            模板 1 分数：
            <strong className="camera-demo__debug-value--blue">
              {templateScorePermille}
            </strong>
            &nbsp;&nbsp; 区域：{debug.templateRegion.x},
            {debug.templateRegion.y},{debug.templateRegion.w},
            {debug.templateRegion.h}；均值 RGB(
            {debug.avgR}, {debug.avgG}, {debug.avgB})；高度：
            {debug.regionHeight}px
          </div>
          <div>
            🧩 模板 2：
            <strong
              className={
                sourceTemplateDetected
                  ? "camera-demo__debug-mode--active"
                  : "camera-demo__debug-mode--inactive"
              }
            >
              {sourceTemplateDetected ? "已命中" : "未命中"}
            </strong>
            &nbsp;&nbsp; 相似度：{sourceTemplateScorePercent}%&nbsp;&nbsp; 连续：
            {sourceTemplateConsecutiveMatches}/2&nbsp;&nbsp; 阈值：
            {debug.sourceTemplateThresholdPercent}%
            {!templateDetected ? "；当前仅展示评分" : ""}
          </div>
          <div>
            模板 2 分数：
            <strong className="camera-demo__debug-value--blue">
              {sourceTemplateScorePermille}
            </strong>
            &nbsp;&nbsp; 区域：{debug.sourceTemplateRegion.x},
            {debug.sourceTemplateRegion.y},{debug.sourceTemplateRegion.w},
            {debug.sourceTemplateRegion.h}
          </div>
          <div className="camera-demo__debug-hint">
            模板 1：在 `(80,900,120,80)` 内滑动匹配 `38x34`，每 1 秒检测一次，连续命中
            3 秒后触发；刷新锁定 {REFRESH_LOCK_MS / 1000} 秒
            {isRefreshLocked ? `，剩余 ${refreshCountdownSeconds} 秒` : ""}
            <br />
            模板 2：对 `(1585,927,38,118)` 区域匹配 `source_template.png`，阈值 80%，连续命中 2 帧后生成拼接图。
          </div>
        </div>
      </div>
    </div>
  );
}
